import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Saved viewport uses grid-unit center coordinates (container-independent)
interface SavedViewport {
  zoom: number;
  panByZone: Record<string, { centerX: number; centerY: number } | { x: number; y: number }>;
}

interface UseFloorPlanViewportOptions {
  establishmentId?: string;
  isEditMode: boolean;
  effectiveGrid: { cols: number; rows: number };
  baseCellSize: number;
  selectedZoneId: string | null;
  savedViewport?: SavedViewport | null;
}

// Check if a saved pan entry uses old pixel format
function isPixelFormat(v: { x?: number; y?: number; centerX?: number; centerY?: number }): v is { x: number; y: number } {
  return 'x' in v && !('centerX' in v);
}

export const useFloorPlanViewport = ({
  establishmentId,
  isEditMode,
  effectiveGrid,
  baseCellSize,
  selectedZoneId,
  savedViewport,
}: UseFloorPlanViewportOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert saved grid-unit center to pixel pan offset for current container
  const savedCenterToPan = useCallback((center: { centerX: number; centerY: number }, zoom: number): { x: number; y: number } => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const cs = baseCellSize * zoom;
    return {
      x: cw / 2 - center.centerX * cs,
      y: ch / 2 - center.centerY * cs,
    };
  }, [baseCellSize]);

  // Convert current pixel pan offset to grid-unit center for DB storage
  const panToSavedCenter = useCallback((pan: { x: number; y: number }, zoom: number): { centerX: number; centerY: number } => {
    if (!containerRef.current) return { centerX: 0, centerY: 0 };
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const cs = baseCellSize * zoom;
    return {
      centerX: (cw / 2 - pan.x) / cs,
      centerY: (ch / 2 - pan.y) / cs,
    };
  }, [baseCellSize]);

  // Resolve a saved pan entry (old pixel or new grid-unit) to pixel offset
  const resolveSavedPan = useCallback((entry: { centerX?: number; centerY?: number; x?: number; y?: number }, zoom: number): { x: number; y: number } => {
    if (isPixelFormat(entry as any)) {
      return { x: (entry as any).x, y: (entry as any).y };
    }
    return savedCenterToPan(entry as { centerX: number; centerY: number }, zoom);
  }, [savedCenterToPan]);

  // Zoom - use savedViewport from DB first, then localStorage fallback
  const [zoomLevel, setZoomLevel] = useState(1);

  // Pan offsets per zone (always in pixels for runtime use)
  const [panOffsetsByZone, setPanOffsetsByZone] = useState<Record<string, { x: number; y: number }>>({});

  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const [hasPerformedInitialFit, setHasPerformedInitialFit] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasAppliedSavedViewportRef = useRef(false);

  const getCurrentPanKey = useCallback(() => {
    if (selectedZoneId === null) return '_all';
    if (selectedZoneId === 'unassigned') return '_unassigned';
    return selectedZoneId;
  }, [selectedZoneId]);

  const panOffset = useMemo(() => {
    const key = getCurrentPanKey();
    return panOffsetsByZone[key] || { x: 0, y: 0 };
  }, [panOffsetsByZone, getCurrentPanKey]);

  const setPanOffset = useCallback((newOffset: { x: number; y: number } | ((prev: { x: number; y: number }) => { x: number; y: number })) => {
    setPanOffsetsByZone(prev => {
      const key = getCurrentPanKey();
      const currentOffset = prev[key] || { x: 0, y: 0 };
      const resolvedOffset = typeof newOffset === 'function' ? newOffset(currentOffset) : newOffset;
      return { ...prev, [key]: resolvedOffset };
    });
  }, [getCurrentPanKey]);

  const cellSize = baseCellSize * zoomLevel;

  // Apply savedViewport once container is mounted
  useEffect(() => {
    if (!savedViewport?.zoom || !containerRef.current || hasAppliedSavedViewportRef.current) return;
    hasAppliedSavedViewportRef.current = true;
    setZoomLevel(savedViewport.zoom);
    if (savedViewport.panByZone && Object.keys(savedViewport.panByZone).length > 0) {
      const resolved: Record<string, { x: number; y: number }> = {};
      for (const [key, entry] of Object.entries(savedViewport.panByZone)) {
        resolved[key] = resolveSavedPan(entry, savedViewport.zoom);
      }
      setPanOffsetsByZone(resolved);
    }
  }, [savedViewport, resolveSavedPan]);

  // Persist zoom
  useEffect(() => {
    if (establishmentId && zoomLevel !== 1) {
      localStorage.setItem(`floorplan-zoom-${establishmentId}`, zoomLevel.toString());
    }
  }, [zoomLevel, establishmentId]);

  // Persist pan offsets
  useEffect(() => {
    if (establishmentId && Object.keys(panOffsetsByZone).length > 0) {
      localStorage.setItem(`floorplan-pan-zones-${establishmentId}`, JSON.stringify(panOffsetsByZone));
    }
  }, [panOffsetsByZone, establishmentId]);

  // Load stored zoom/pan when establishmentId changes — prefer savedViewport from DB
  useEffect(() => {
    if (!establishmentId) return;
    if (savedViewport?.zoom && containerRef.current) {
      setZoomLevel(savedViewport.zoom);
      if (savedViewport.panByZone && Object.keys(savedViewport.panByZone).length > 0) {
        const resolved: Record<string, { x: number; y: number }> = {};
        for (const [key, entry] of Object.entries(savedViewport.panByZone)) {
          resolved[key] = resolveSavedPan(entry, savedViewport.zoom);
        }
        setPanOffsetsByZone(resolved);
      }
    } else if (!savedViewport?.zoom) {
      const storedZoom = localStorage.getItem(`floorplan-zoom-${establishmentId}`);
      if (storedZoom) setZoomLevel(parseFloat(storedZoom));
      const storedPanZones = localStorage.getItem(`floorplan-pan-zones-${establishmentId}`);
      if (storedPanZones) {
        try { setPanOffsetsByZone(JSON.parse(storedPanZones)); } catch { /* ignore */ }
      }
    }
  }, [establishmentId, savedViewport, resolveSavedPan]);

  // Restore anchored viewport — recomputes pixel offsets from grid-unit centers
  const restoreAnchoredViewport = useCallback(() => {
    if (!containerRef.current) return;
    // If saved viewport exists, restore from it (recompute pixel offsets for current container)
    if (savedViewport?.zoom) {
      setZoomLevel(savedViewport.zoom);
      if (savedViewport.panByZone && Object.keys(savedViewport.panByZone).length > 0) {
        const resolved: Record<string, { x: number; y: number }> = {};
        for (const [key, entry] of Object.entries(savedViewport.panByZone)) {
          resolved[key] = resolveSavedPan(entry, savedViewport.zoom);
        }
        setPanOffsetsByZone(resolved);
      }
      return;
    }
    // Fallback: fit to container
    const containerWidth = containerRef.current.clientWidth - 32;
    const containerHeight = containerRef.current.clientHeight - 32;
    const gridWidth = effectiveGrid.cols * baseCellSize;
    const gridHeight = effectiveGrid.rows * baseCellSize;
    const scaleX = containerWidth / gridWidth;
    const scaleY = containerHeight / gridHeight;
    const finalZoom = Math.max(0.1, Math.min(Math.min(scaleX, scaleY), 2));
    setZoomLevel(finalZoom);
    setPanOffset({ x: 0, y: 0 });
    if (establishmentId) {
      localStorage.setItem(`floorplan-zoom-${establishmentId}`, finalZoom.toString());
    }
  }, [effectiveGrid, baseCellSize, establishmentId, setPanOffset, savedViewport, resolveSavedPan]);

  // Reset view
  const handleResetView = useCallback(() => {
    if (savedViewport?.zoom && containerRef.current) {
      setZoomLevel(savedViewport.zoom);
      if (savedViewport.panByZone) {
        const resolved: Record<string, { x: number; y: number }> = {};
        for (const [key, entry] of Object.entries(savedViewport.panByZone)) {
          resolved[key] = resolveSavedPan(entry, savedViewport.zoom);
        }
        setPanOffsetsByZone(resolved);
      }
    } else {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
      if (establishmentId) {
        localStorage.removeItem(`floorplan-zoom-${establishmentId}`);
        const key = getCurrentPanKey();
        setPanOffsetsByZone(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }
    }
  }, [establishmentId, getCurrentPanKey, setPanOffset, savedViewport, resolveSavedPan]);

  const handleFitToScreen = useCallback(() => {
    setIsExpanded(prev => {
      const next = !prev;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restoreAnchoredViewport();
        });
      });
      return next;
    });
  }, [restoreAnchoredViewport]);

  // Collapse back to normal size
  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        restoreAnchoredViewport();
      });
    });
  }, [restoreAnchoredViewport]);

  // Auto-fit on first load for new establishments (skip if savedViewport exists)
  // No-op: plan renders at saved or default zoom/pan, no auto-fit
  const performInitialFit = useCallback((_positionsLoading: boolean, _positionsCount: number) => {
    return;
  }, []);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const floorPlanTable = target.closest('[data-floor-plan-table]');
    if (floorPlanTable && isEditMode) return;
    const draggableTable = target.closest('[draggable="true"]');
    if (draggableTable && !isEditMode) return;

    if (isEditMode) {
      if (e.button !== 1 && !e.altKey) return;
    } else {
      if (e.button !== 0 && e.button !== 1 && !e.altKey) return;
    }

    e.preventDefault();
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y,
    };
  }, [isEditMode, panOffset]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning || !panStartRef.current) return;
    const deltaX = e.clientX - panStartRef.current.x;
    const deltaY = e.clientY - panStartRef.current.y;
    setPanOffset({
      x: panStartRef.current.offsetX + deltaX,
      y: panStartRef.current.offsetY + deltaY,
    });
  }, [isPanning, setPanOffset]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  // Center on a specific table
  const handleCenterOnTable = useCallback((tableX: number, tableY: number, tableWidth: number, tableHeight: number) => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const centerX = tableX * cellSize + (tableWidth * cellSize) / 2;
    const centerY = tableY * cellSize + (tableHeight * cellSize) / 2;
    setPanOffset({
      x: containerWidth / 2 - centerX,
      y: containerHeight / 2 - centerY,
    });
  }, [cellSize, setPanOffset]);

  // Wheel-to-zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!isEditMode && !e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const contentX = (cursorX - panOffset.x) / cellSize;
    const contentY = (cursorY - panOffset.y) / cellSize;

    const delta = -e.deltaY * 0.002;
    const newZoom = Math.max(0.1, Math.min(zoomLevel + delta * zoomLevel, 3));
    const newCellSize = baseCellSize * newZoom;

    const newPanX = cursorX - contentX * newCellSize;
    const newPanY = cursorY - contentY * newCellSize;

    setZoomLevel(newZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  }, [zoomLevel, cellSize, baseCellSize, panOffset, setPanOffset]);

  // === Pinch-to-zoom (iPad / touch devices) ===
  const pinchRef = useRef<{ startDist: number; startZoom: number; midX: number; midY: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const rect = containerRef.current?.getBoundingClientRect();
      pinchRef.current = {
        startDist: dist,
        startZoom: zoomLevel,
        midX: (e.touches[0].clientX + e.touches[1].clientX) / 2 - (rect?.left || 0),
        midY: (e.touches[0].clientY + e.touches[1].clientY) / 2 - (rect?.top || 0),
      };
    } else if (e.touches.length === 1 && !isEditMode) {
      setIsPanning(true);
      panStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        offsetX: panOffset.x,
        offsetY: panOffset.y,
      };
    }
  }, [zoomLevel, panOffset, isEditMode]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / pinchRef.current.startDist;
      const newZoom = Math.max(0.1, Math.min(pinchRef.current.startZoom * scale, 3));
      const newCellSize = baseCellSize * newZoom;
      const oldCellSize = baseCellSize * pinchRef.current.startZoom;

      const contentX = (pinchRef.current.midX - panOffset.x) / oldCellSize;
      const contentY = (pinchRef.current.midY - panOffset.y) / oldCellSize;
      const newPanX = pinchRef.current.midX - contentX * newCellSize;
      const newPanY = pinchRef.current.midY - contentY * newCellSize;

      setZoomLevel(newZoom);
      setPanOffset({ x: newPanX, y: newPanY });
    } else if (e.touches.length === 1 && isPanning && panStartRef.current) {
      const deltaX = e.touches[0].clientX - panStartRef.current.x;
      const deltaY = e.touches[0].clientY - panStartRef.current.y;
      setPanOffset({
        x: panStartRef.current.offsetX + deltaX,
        y: panStartRef.current.offsetY + deltaY,
      });
    }
  }, [isPanning, baseCellSize, panOffset, setPanOffset]);

  const handleTouchEnd = useCallback(() => {
    pinchRef.current = null;
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  // Get current viewport state for saving to DB — converts pixel offsets to grid-unit centers
  const getViewportState = useCallback((): SavedViewport => {
    const savedPanByZone: Record<string, { centerX: number; centerY: number }> = {};
    for (const [key, pan] of Object.entries(panOffsetsByZone)) {
      savedPanByZone[key] = panToSavedCenter(pan, zoomLevel);
    }
    return {
      zoom: zoomLevel,
      panByZone: savedPanByZone,
    };
  }, [zoomLevel, panOffsetsByZone, panToSavedCenter]);

  return {
    containerRef,
    zoomLevel,
    setZoomLevel,
    cellSize,
    panOffset,
    setPanOffset,
    isPanning,
    isExpanded,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResetView,
    handleFitToScreen,
    handleCollapse,
    performInitialFit,
    handleCenterOnTable,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getViewportState,
  };
};
